import { useRef, useState, useCallback } from 'react';

/**
 * Odsunutí toastu přejetím nahoru.
 *
 * Toast visí u horní hrany, takže se zavírá tudy, kam míří — nahoru. Je to
 * totéž gesto, jakým se odklízí notifikace v telefonu, a hlavně nevyžaduje
 * trefit křížek: ten má zásah ~28 px a stojí v rohu, kam se palcem při držení
 * jednou rukou nedosáhne.
 *
 * Protistrana je `useBottomSheetSwipe` — ten zavírá tahem dolů, protože sheet
 * leží u spodní hrany. Tady je práh nižší (48 px): toast je nízký a člověk ho
 * odklízí mimochodem, ne rozmyslně.
 *
 * Vrací:
 *   - `props`: rozprostřít na obal toastu (celá plocha, ne úchyt — na toastu
 *     není co scrollovat, takže gesto nemá s čím soupeřit)
 *   - `style`: transform a krytí během tahu, snap-back po puštění
 *
 * Klepnutí na toast otevírá notifikaci, takže po tahu se první klik zahodí
 * (`onClickCapture`) — jinak by odsunutí zároveň někam navigovalo.
 */
export default function useToastSwipe(onClose, { threshold = 48, enabled = true } = {}) {
    const startY = useRef(null);
    const tazeno = useRef(false);
    const [dragY, setDragY] = useState(0);
    const [dragging, setDragging] = useState(false);
    const [odchazi, setOdchazi] = useState(false);

    const onTouchStart = useCallback((e) => {
        if (!enabled || odchazi) return;
        startY.current = e.touches[0].clientY;
        tazeno.current = false;
        setDragging(true);
    }, [enabled, odchazi]);

    const onTouchMove = useCallback((e) => {
        if (!enabled || startY.current == null) return;
        const dy = e.touches[0].clientY - startY.current;
        if (Math.abs(dy) > 6) tazeno.current = true;
        // Nahoru volně, dolů jen na třetinu — toast je u horní hrany a dolů
        // nemá kam odejít; odpor říká „tudy ne", aniž by gesto zablokoval.
        setDragY(dy < 0 ? dy : dy / 3);
    }, [enabled]);

    const finalize = useCallback(() => {
        if (!enabled || startY.current == null) return;
        setDragging(false);
        startY.current = null;
        if (-dragY > threshold) {
            // Dojet nahoru a teprve pak zmizet — skok by vypadal jako chyba.
            setOdchazi(true);
            setTimeout(() => onClose?.(), 160);
            return;
        }
        setDragY(0);
    }, [enabled, dragY, threshold, onClose]);

    const onClickCapture = useCallback((e) => {
        if (!tazeno.current) return;
        tazeno.current = false;
        e.stopPropagation();
        e.preventDefault();
    }, []);

    const props = enabled
        ? { onTouchStart, onTouchMove, onTouchEnd: finalize, onTouchCancel: finalize, onClickCapture }
        : {};

    const posun = odchazi ? -200 : dragY;
    const style = enabled
        ? {
            transform: posun ? `translateY(${posun}px)` : undefined,
            opacity: odchazi ? 0 : (dragY < 0 ? Math.max(0.25, 1 + dragY / 140) : 1),
            transition: dragging ? 'none' : 'transform 0.22s ease, opacity 0.22s ease',
            touchAction: 'none',
        }
        : {};

    return { props, style, dragging };
}
